import "./SearchCheckList.css";

import { useState, useRef, useEffect, useId } from "react";

import { Checkbox } from "./Checkbox";

import Tags from "./Tags";

import Input from "./Input";

const labelText = (label) =>
  typeof label === "string" || typeof label === "number" ? String(label) : "";

const SearchCheckList = ({
  label,
  options = [],
  selected = [],
  onChange,
  fullWidth = "",
  placeholder = "Select",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState("");

  const ref = useRef();
  const triggerRef = useRef(null);
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const boxId = `${baseId}-box`;

  // Only listens while the list is open.
  useEffect(() => {
    if (!open) return undefined;

    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const query = search.toLowerCase();
  const filtered = query
    ? options.filter((item) => labelText(item.label).toLowerCase().includes(query))
    : options;

  const toggle = (value) => {
    if (disabled) return;
    const exists = selected.includes(value);

    if (exists) {
      onChange(selected.filter((x) => x !== value));

      return;
    }

    onChange([...selected, value]);
  };

  // Keys pressed on the tag remove buttons inside the trigger are theirs.
  const handleTriggerKeyDown = (e) => {
    if (disabled || e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((prev) => !prev);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleBoxKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div className={`search-dropdown ${fullWidth}`} ref={ref}>
      {label && (
        <label className="ui-select-label" id={labelId}>
          {label}
        </label>
      )}
      <div
        ref={triggerRef}
        className={`search-dropdown-trigger ${disabled ? "disabled" : ""}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="true"
        aria-expanded={open && !disabled}
        aria-controls={open && !disabled ? boxId : undefined}
        aria-disabled={disabled || undefined}
        aria-labelledby={label ? labelId : undefined}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={handleTriggerKeyDown}
      >
        <div className="selected-tags">
          {selected.length === 0 ? (
            <span>{placeholder}</span>
          ) : (
            selected.map((value) => {
              const item = options.find((x) => x.value === value);

              return (
                <Tags
                  key={value}
                  size="sm"
                  closable
                  onClose={(e) => {
                    e.stopPropagation();

                    toggle(value);
                  }}
                >
                  {item?.label}
                </Tags>
              );
            })
          )}
        </div>

        <span aria-hidden="true">{open ? "▲" : "▼"}</span>
      </div>

      {open && !disabled && (
        <div className="search-dropdown-box" id={boxId} onKeyDown={handleBoxKeyDown}>
          <Input
            className="dropdown-search-input"
            placeholder="Search..."
            aria-label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="dropdown-options">
            {filtered.map((item) => (
              <div key={item.value} className="dropdown-option">
                {/* The label sits inside the checkbox's <label> so clicking the
                    text toggles it and screen readers announce it. */}
                <Checkbox
                  checked={selected.includes(item.value)}
                  onChange={() => toggle(item.value)}
                  label={<span>{item.label}</span>}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchCheckList;
